import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { Badge } from "@workspace/amarmeds-ds/components/ui/badge";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";
import { Input } from "@workspace/amarmeds-ds/components/ui/input";

import { api, ApiError, type OpsPharmacy, type OpsPrice } from "../api";
import { parseTaka, taka } from "../format";

export default function OpsPharmacyPrices({ pharmacyId }: { pharmacyId: string }) {
  const id = Number(pharmacyId);
  const qc = useQueryClient();
  const pharmacies = useQuery({ queryKey: ["ops-pharmacies"], queryFn: () => api<{ pharmacies: OpsPharmacy[] }>("/ops/pharmacies") });
  const pharmacy = pharmacies.data?.pharmacies.find((p) => p.id === id);

  const [search, setSearch] = useState("");
  const q = search.trim();
  const [replace, setReplace] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const prices = useQuery({
    queryKey: ["ops-prices", id, q.length >= 2 ? q : ""],
    queryFn: () => api<{ pharmacyId: number; total?: number; prices: OpsPrice[] }>(`/ops/pharmacies/${id}/prices${q.length >= 2 ? `?q=${encodeURIComponent(q)}` : ""}`),
  });

  const importCsv = useMutation({
    mutationFn: (csv: string) => api(`/ops/pharmacies/${id}/prices/import`, { body: { csv, replace } }),
    onSuccess: (res: { imported: number; skipped: number; replace: boolean }) => {
      setImportResult(`Imported ${res.imported} prices${res.skipped ? `, skipped ${res.skipped} lines` : ""}${res.replace ? " (replaced)" : ""}.`);
      setImportError(null);
      if (fileRef.current) fileRef.current.value = "";
      void qc.invalidateQueries({ queryKey: ["ops-prices"] });
    },
    onError: (err) => {
      setImportResult(null);
      setImportError(err instanceof ApiError ? err.code.replace(/_/g, " ") : "Could not import the file.");
    },
  });

  const onFile = (file: File | undefined) => {
    if (!file) return;
    file.text().then((csv) => importCsv.mutate(csv)).catch(() => setImportError("Could not read the file."));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold">{pharmacy ? `${pharmacy.name} — prices` : `Pharmacy #${id} — prices`}</h1>
        <Link href="/ops/pharmacies">
          <Button size="sm" variant="outline">
            Back
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Import a price list (CSV)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Columns: <span className="font-mono">medicine_id,strip_price,in_stock</span>. Price is taka for one strip/bottle; <span className="font-mono">in_stock</span> is yes/no.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => onFile(e.target.files?.[0])}
              className="text-sm"
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" className="h-4 w-4" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
              Replace (mark missing as out of stock)
            </label>
            {importCsv.isPending && <span className="text-sm text-muted-foreground">Importing…</span>}
          </div>
          {importResult && <p className="text-sm">{importResult}</p>}
          {importError && <p className="text-sm text-destructive">{importError}</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Prices</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Search a medicine by brand or generic name" value={search} onChange={(e) => setSearch(e.target.value)} />
          {q.length === 1 && <p className="text-sm text-muted-foreground">Type at least two letters.</p>}
          {prices.data && !q && prices.data.total !== undefined && (
            <p className="text-sm text-muted-foreground">
              {prices.data.total} priced medicine{prices.data.total === 1 ? "" : "s"}
              {prices.data.total > prices.data.prices.length ? " (showing the first 200)" : ""}
            </p>
          )}
          <div className="space-y-2">
            {prices.data?.prices.map((p) => (
              <PriceRow key={p.medicineId} pharmacyId={id} price={p} />
            ))}
            {prices.data && prices.data.prices.length === 0 && <p className="text-sm text-muted-foreground">Nothing found.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function PriceRow({ pharmacyId, price }: { pharmacyId: number; price: OpsPrice }) {
  const qc = useQueryClient();
  const [text, setText] = useState(price.stripPricePaisa === null ? "" : String(price.stripPricePaisa / 100));
  const [inStock, setInStock] = useState(price.inStock);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () =>
      api(`/ops/pharmacies/${pharmacyId}/prices/${price.medicineId}`, { method: "PATCH", body: { stripPricePaisa: parseTaka(text), inStock } }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ["ops-prices"] });
    },
    onError: (err) => setError(err instanceof ApiError ? err.code.replace(/_/g, " ") : "Could not save."),
  });
  const valid = parseTaka(text) !== null;
  return (
    <div className="flex flex-wrap items-center gap-3 border-b pb-2 text-sm last:border-0">
      <div className="min-w-48 flex-1">
        <div className="font-medium">{price.brandName}</div>
        <div className="text-muted-foreground">
          {[price.genericName, price.strength, price.dosageForm].filter(Boolean).join(" · ")} · #{price.medicineId}
        </div>
      </div>
      {price.stripPricePaisa !== null && <Badge variant="outline">now {taka(price.stripPricePaisa)}</Badge>}
      <div className="flex items-center gap-2">
        <Input className="w-24" inputMode="decimal" placeholder="৳" value={text} onChange={(e) => setText(e.target.value)} />
        <label className="flex items-center gap-1">
          <input type="checkbox" className="h-4 w-4" checked={inStock} onChange={(e) => setInStock(e.target.checked)} />
          In stock
        </label>
        <Button size="sm" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
